import type { LocationCoordinate, AddressInfo } from '@/store/location';

// 네이버 지도 API 응답 타입 (사용하지 않음)
// interface NaverGeocodingResponse {
//   status: string;
//   meta: {
//     totalCount: number;
//     page: number;
//     count: number;
//   };
//   addresses: Array<{
//     roadAddress: string;
//     jibunAddress: string;
//     englishAddress: string;
//     x: string; // 경도 (longitude)
//     y: string; // 위도 (latitude)
//     distance: number;
//     addressElements: Array<{
//       types: string[];
//       longName: string;
//       shortName: string;
//       code: string;
//     }>;
//   }>;
//   errorMessage?: string;
// }

// 사용하지 않는 NaverReverseGeocodingResponse 제거

class LocationService {
  private readonly naverClientId: string;

  constructor() {
    this.naverClientId = import.meta.env.VITE_NAVER_CLIENT_ID || '';
    
    if (!this.naverClientId) {
      console.warn('네이버 지도 API 클라이언트 ID가 설정되지 않았습니다.');
    }
  }

  /**
   * 좌표를 주소로 변환 (Reverse Geocoding)
   */
  async reverseGeocode(coordinate: LocationCoordinate): Promise<AddressInfo> {
    try {
      
      const { latitude, longitude } = coordinate;
      
      // Supabase Edge Function 호출
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseAnonKey) {
        console.warn('Supabase 설정이 없어 임시 주소를 사용합니다.');
        return this.generateAddressFromCoordinate(coordinate);
      }
      
      const response = await fetch(
        `${supabaseUrl}/functions/v1/reverse-geocode`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
          body: JSON.stringify({ latitude, longitude }),
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Edge Function 응답:', errorText);
        throw new Error(`Edge Function 호출 실패: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      
      return {
        roadAddress: data.roadAddress,
        jibunAddress: data.jibunAddress,
        zonecode: undefined
      };
      
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      // 에러 시 좌표 기반 기본 주소 반환
      return this.generateAddressFromCoordinate(coordinate);
    }
  }

  /**
   * 좌표 기반 임시 주소 생성
   * 실제 서비스에서는 Supabase Edge Function으로 네이버 API 호출 필요
   */
  private async generateAddressFromCoordinate(coordinate: LocationCoordinate): Promise<AddressInfo> {
    const { latitude, longitude } = coordinate;
    
    // 주요 지역별 상세 구역 설정
    let region = '';
    let district = '';
    let dong = '';
    
    // 서울특별시 지역
    if (latitude >= 37.4 && latitude <= 37.7 && longitude >= 126.8 && longitude <= 127.2) {
      region = '서울특별시';
      
      // 강남구 지역 (더 세밀하게 구분)
      if (latitude >= 37.48 && latitude <= 37.54 && longitude >= 127.01 && longitude <= 127.08) {
        district = '강남구';
        // 삼성동 (코엑스, 봉은사역 일대)
        if (latitude >= 37.508 && latitude <= 37.515 && longitude >= 127.054 && longitude <= 127.065) {
          dong = '삼성동';
        } 
        // 대치동 (대치역, 도곡역 일대)
        else if (latitude >= 37.493 && latitude <= 37.505 && longitude >= 127.057 && longitude <= 127.070) {
          dong = '대치동';
        } 
        // 역삼동 (강남역, 역삼역 일대)
        else if (latitude >= 37.493 && latitude <= 37.503 && longitude >= 127.028 && longitude <= 127.043) {
          dong = '역삼동';
        } 
        // 논현동 (학동역, 논현역 일대)
        else if (latitude >= 37.507 && latitude <= 37.518 && longitude >= 127.022 && longitude <= 127.035) {
          dong = '논현동';
        }
        // 청담동 (청담역 일대)
        else if (latitude >= 37.517 && latitude <= 37.527 && longitude >= 127.047 && longitude <= 127.058) {
          dong = '청담동';
        }
        // 개포동 (개포동역 일대)
        else if (latitude >= 37.485 && latitude <= 37.492 && longitude >= 127.065 && longitude <= 127.075) {
          dong = '개포동';
        }
        // 일원동 (일원역 일대)
        else if (latitude >= 37.483 && latitude <= 37.491 && longitude >= 127.082 && longitude <= 127.094) {
          dong = '일원동';
        }
        // 수서동 (수서역 일대)
        else if (latitude >= 37.482 && latitude <= 37.492 && longitude >= 127.095 && longitude <= 127.108) {
          dong = '수서동';
        }
        // 세곡동
        else if (latitude >= 37.465 && latitude <= 37.475 && longitude >= 127.095 && longitude <= 127.110) {
          dong = '세곡동';
        }
        else {
          dong = '강남구';
        }
      }
      // 서초구 지역
      else if (latitude >= 37.47 && latitude <= 37.50 && longitude >= 126.99 && longitude <= 127.04) {
        district = '서초구';
        if (latitude >= 37.48 && latitude <= 37.49 && longitude >= 127.01 && longitude <= 127.03) {
          dong = '서초동';
        } else if (latitude >= 37.49 && latitude <= 37.50 && longitude >= 126.99 && longitude <= 127.01) {
          dong = '방배동';
        } else {
          dong = '양재동';
        }
      }
      // 마포구 지역
      else if (latitude >= 37.54 && latitude <= 37.58 && longitude >= 126.90 && longitude <= 126.96) {
        district = '마포구';
        if (latitude >= 37.55 && latitude <= 37.56 && longitude >= 126.92 && longitude <= 126.94) {
          dong = '홍대입구역';
        } else if (latitude >= 37.54 && latitude <= 37.55 && longitude >= 126.94 && longitude <= 126.96) {
          dong = '공덕동';
        } else {
          dong = '상수동';
        }
      }
      // 종로구 지역
      else if (latitude >= 37.57 && latitude <= 37.60 && longitude >= 126.97 && longitude <= 127.01) {
        district = '종로구';
        if (latitude >= 37.57 && latitude <= 37.58 && longitude >= 126.97 && longitude <= 126.99) {
          dong = '광화문';
        } else {
          dong = '종로';
        }
      }
      // 중구 지역
      else if (latitude >= 37.55 && latitude <= 37.57 && longitude >= 126.97 && longitude <= 127.00) {
        district = '중구';
        dong = '명동';
      }
      // 용산구 지역
      else if (latitude >= 37.52 && latitude <= 37.55 && longitude >= 126.96 && longitude <= 127.00) {
        district = '용산구';
        dong = '이태원동';
      }
      // 그 외 서울 지역
      else {
        district = '서울시';
        dong = '';
      }
    }
    // 경기도 지역
    else if (latitude >= 37.2 && latitude <= 37.5 && longitude >= 126.7 && longitude <= 127.2) {
      region = '경기도';
      // 성남시
      if (latitude >= 37.35 && latitude <= 37.47 && longitude >= 127.10 && longitude <= 127.20) {
        district = '성남시';
        if (latitude >= 37.40 && latitude <= 37.45) {
          dong = '분당구';
        } else {
          dong = '수정구';
        }
      }
      // 수원시
      else if (latitude >= 37.24 && latitude <= 37.34 && longitude >= 126.95 && longitude <= 127.08) {
        district = '수원시';
        dong = '영통구';
      }
      // 고양시
      else if (latitude >= 37.58 && latitude <= 37.70 && longitude >= 126.75 && longitude <= 126.98) {
        district = '고양시';
        dong = '일산동구';
      }
      else {
        district = '경기도';
        dong = '';
      }
    }
    // 부산광역시 지역
    else if (latitude >= 35.0 && latitude <= 35.3 && longitude >= 128.9 && longitude <= 129.3) {
      region = '부산광역시';
      if (latitude >= 35.15 && latitude <= 35.20 && longitude >= 129.05 && longitude <= 129.10) {
        district = '해운대구';
        dong = '해운대해수욕장';
      } else if (latitude >= 35.10 && latitude <= 35.15 && longitude >= 129.02 && longitude <= 129.07) {
        district = '부산진구';
        dong = '서면';
      } else {
        district = '부산시';
        dong = '';
      }
    }
    // 그 외 지역
    else {
      region = '대한민국';
      district = '';
      dong = '';
    }
    
    // 주소 조합
    let roadAddress = region;
    let jibunAddress = region;
    
    if (district) {
      roadAddress += ` ${district}`;
      jibunAddress += ` ${district}`;
    }
    
    if (dong) {
      roadAddress += ` ${dong}`;
      jibunAddress += ` ${dong}`;
    }
    
    // 동이 없으면 "일대" 추가
    if (!dong && district) {
      roadAddress += ' 일대';
      jibunAddress += ' 일대';
    }
    
    return {
      roadAddress,
      jibunAddress,
      zonecode: undefined
    };
  }

  /**
   * 주소를 좌표로 변환 (Geocoding)
   */
  async geocode(address: string): Promise<LocationCoordinate> {
    try {
      console.log('Geocoding address:', address);
      
      // Supabase Edge Function 호출
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseAnonKey) {
        console.warn('Supabase 설정이 없어 임시 좌표를 사용합니다.');
        // 강남구 기본 좌표 반환
        return {
          latitude: 37.5173,
          longitude: 127.0473
        };
      }
      
      const response = await fetch(
        `${supabaseUrl}/functions/v1/geocode`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
          body: JSON.stringify({ address }),
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Edge Function 응답:', errorText);
        throw new Error(`Edge Function 호출 실패: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Edge Function 전체 응답:', data);
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      // Edge Function 응답 형식에 맞게 파싱
      if (data.success && data.data) {
        console.log('Geocoding 성공:', {
          latitude: data.data.latitude,
          longitude: data.data.longitude,
          confidence: data.data.confidence
        });
        
        return {
          latitude: data.data.latitude,
          longitude: data.data.longitude
        };
      } else {
        throw new Error('Geocoding 응답 형식이 올바르지 않습니다');
      }
      
    } catch (error) {
      console.error('Geocoding failed:', error);
      
      // 에러 시 주소별 기본 좌표 반환
      if (address.includes('강남')) {
        return { latitude: 37.5173, longitude: 127.0473 };
      } else if (address.includes('서초')) {
        return { latitude: 37.4837, longitude: 127.0324 };
      } else if (address.includes('마포')) {
        return { latitude: 37.5547, longitude: 126.9707 };
      } else if (address.includes('종로')) {
        return { latitude: 37.5735, longitude: 126.9790 };
      } else if (address.includes('중구')) {
        return { latitude: 37.5640, longitude: 126.9975 };
      } else {
        // 서울시청 기본 좌표
        return { latitude: 37.5666805, longitude: 126.9784147 };
      }
    }
  }

  /**
   * 두 좌표 간의 거리 계산 (Haversine 공식)
   */
  calculateDistance(coord1: LocationCoordinate, coord2: LocationCoordinate): number {
    const R = 6371; // 지구 반지름 (km)
    const dLat = this.toRad(coord2.latitude - coord1.latitude);
    const dLon = this.toRad(coord2.longitude - coord1.longitude);
    
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(coord1.latitude)) * Math.cos(this.toRad(coord2.latitude)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // km 단위
    
    return Math.round(distance * 1000); // 미터 단위로 반환
  }

  /**
   * 각도를 라디안으로 변환
   */
  private toRad(value: number): number {
    return value * Math.PI / 180;
  }

  // 사용하지 않는 buildRoadAddress, buildJibunAddress 함수 제거

  /**
   * 기본 위치 가져오기 (매장이 가장 많은 지역)
   * 실제 구현에서는 Supabase에서 매장 데이터를 분석해야 함
   */
  async getDefaultLocation(): Promise<{ coordinate: LocationCoordinate; address: AddressInfo }> {
    // 임시로 서울 강남구를 기본값으로 설정
    const defaultCoordinate: LocationCoordinate = {
      latitude: 37.5173, // 강남구청 근처
      longitude: 127.0473
    };

    try {
      const address = await this.reverseGeocode(defaultCoordinate);
      return {
        coordinate: defaultCoordinate,
        address
      };
    } catch (error) {
      // Reverse geocoding 실패 시 하드코딩된 주소 반환
      return {
        coordinate: defaultCoordinate,
        address: {
          roadAddress: '서울특별시 강남구 테헤란로 152',
          jibunAddress: '서울특별시 강남구 역삼동 736'
        }
      };
    }
  }

}

export const locationService = new LocationService();